import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Those two passwords are not the same");
      return;
    }
    setBusy(true);
    try {
      await api.post("/security/reset-password", { token, new_password: password });
      toast.success("Password changed — sign in with it now");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F7F7F5] px-5 py-12" data-testid="reset-page">
      <div className="w-full max-w-md border border-border bg-white p-7">
        <p className="overline text-[#B3241B]">Unity &amp; Utility</p>
        <h1 className="font-display mt-2 text-3xl font-bold tracking-tight text-[#0F172A]">
          Choose a new password
        </h1>

        {!token ? (
          <p data-testid="reset-no-token" className="mt-6 border-l-2 border-[#B3241B] bg-[#FFF1F0] px-4 py-3 text-sm text-[#8E1C15]">
            This link is missing its code. Ask for a new reset link.
          </p>
        ) : (
          <form className="mt-6 space-y-4" onSubmit={submit}>
            <p className="text-sm text-slate-600">
              Use at least 10 characters with an uppercase letter, a lowercase letter, a number
              and a symbol. Every device signed in on this account will be signed out.
            </p>
            <div>
              <Label htmlFor="password" className="overline text-slate-500">New password</Label>
              <Input id="password" data-testid="reset-password-input" type="password" value={password} required
                onChange={(e) => setPassword(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="confirm" className="overline text-slate-500">Again, to be sure</Label>
              <Input id="confirm" data-testid="reset-confirm-input" type="password" value={confirm} required
                onChange={(e) => setConfirm(e.target.value)} className="mt-1" />
            </div>
            {error && (
              <p data-testid="reset-error" className="border-l-2 border-[#B3241B] bg-[#FFF1F0] px-3 py-2 text-sm text-[#8E1C15]">{error}</p>
            )}
            <Button type="submit" data-testid="reset-submit" className="w-full" disabled={busy || password.length < 10}>
              <LockKey size={16} className="mr-1.5" /> {busy ? "Saving…" : "Save my new password"}
            </Button>
          </form>
        )}

        <p className="mt-6 text-sm text-slate-600">
          <Link to="/forgot-password" data-testid="reset-ask-again" className="tap-safe font-semibold text-[#002FA7] hover:underline">
            Send me another link
          </Link>
        </p>
      </div>
    </div>
  );
}
